import React, { MutableRefObject, useContext, useEffect, useRef, useState } from 'react';
import { useTypedFastStorage } from '../../../../hooks/useFastStorage';
import SecondaryButton from "../../../../ui/SecondaryButton";
import DateControl from "../../../../ui/Date";
import _ from "lodash";
import Label from '../../../../ui/Label';
import ApplicationContext, { IApplicationContext } from '../../../../ApplicationContext';
import { addDays, addMonths, getDate, now } from '../../../../dateUtils';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import { historyUpdate } from '../../../../fms.data/modules/FmsModFunc';
import useGlobalYesNoDialog from '../../../../hooks/useGlobalYesNoDialog';
import { SQLField, SQLInfo, SQLType } from '../../../../framework.core/data/SQLInfo';
import { SaveNewJournalByTask } from '../../../../fms.data/modules/FmsModAccount';
import { JobNames } from '../../../../framework.core/JobNames';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import useSystemSettings from '../../../../hooks/useSystemSettings';
import { AutoBillingHD } from '../../../../fms.core/AutoBillingHD';
import InlineInputControlRow from '../../../../ui/layout/InlineInputControlRow';
import TextBox from '../../../../ui/TextBox';
import MultilineTextBox from '../../../../ui/MultilineTextBox';
import PrimaryButton from '../../../../ui/PrimaryButton';
import { ApplicableTableSettings } from '../../../../tableSettings';
import { autoBillingDetailTable, autoInvoiceDetailTable } from '../../tableSettings';
import { AotherTemp, AutoBillingDetail, AutoInvoiceDetailList } from '../../../../fms.core/AutoBillingDetail';
import { FloatRenderer, IndicatorRenderer, TextAlignLeftRenderer, TextAlignRightRenderer } from '../../../../uiControls/SmartTableRenderers';
import SmartTable from '../../../../uiControls/SmartTable';
import { FmsAutoNumberType } from '../../../../fms.core/constants';
import { getFmsGlobal } from '../../../../globals';
import { saveAcctHistory } from '../../../../fms.data/modules/FmsModEdi';
import useSetEditedId from '../../../../hooks/useSetEditedId';
import { ApiResult, InsertResultsWithData } from '../../../../framework.core/data/ApiResult';
import Float from '../../../../ui/Float';
import EditableTable, { CellData, TableCellAligns, TableCellTypes } from '../../../../ui/tables/editing/EditableTable';


interface ICntrListItem extends AutoBillingDetail {
  changeLineItem: (change: Partial<ICntrListItem>, index: number) => void,

  [key: string]: any
}

interface AutoBillingStorageKeys {
  modelStorage: string,
};



interface AutoBillingLoadDetailViewParams {
  onPopupClose: () => void,
};

interface AutoBillingLoadedDetailsViewParams {
  appContext: IApplicationContext,
  onPopupClose: () => void,
  autoBilling: AutoBillingHD,
  autoBillingRef: MutableRefObject<AutoBillingHD | null>,
  setAutoBilling: (x: Partial<AutoBillingHD>) => void,
};



function getAutoBillinStorageKeys(): AutoBillingStorageKeys {
  return { modelStorage: 'newAutoBilling' }
}



function AutoBillingImpl(params: AutoBillingLoadDetailViewParams) {
  const { modelStorage } = getAutoBillinStorageKeys();
  const [inStorage, setInStorage, removeInStorage, autoBillingRef] = useTypedFastStorage<AutoBillingHD>(modelStorage, null);
  const [initialized, setInitialized] = useState(false);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const appContext = useContext(ApplicationContext);

  useFunctionKeys({
    onEsc: () => {
      if (initialized)
        return cancel();
      else return false;
    },
  });

  function cancel() {
    const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
    if (modals.length > 0) {
      return false;
    }

    close();
    return true;
  }

  useEffect(() => {
    initialize();
  },
    []
  );

  const newAutoBillingHD = new AutoBillingHD();


  async function initialize() {
    if (!inStorage) {
      await loadExisting();
    }
    setInitialized(true);
  }

  async function loadExisting() {

    const entity = _.cloneDeep(newAutoBillingHD);
    entity.invoiceDate = now();

    setInStorage(entity);
  }


  if (!initialized || !inStorage) return null;

  function close() {
    removeInStorage();
    params.onPopupClose();
  }

  return (
    <AutoBillingInitializedImpl
      autoBilling={inStorage}
      autoBillingRef={autoBillingRef}
      setAutoBilling={setInStorage}
      onPopupClose={close}
      appContext={appContext}
    />
  );
}

function calculateAmount(rate: number | null, qty: number | null) {
  if (rate === null || qty === null) {
    return 0;
  }

  return rate * qty;
}


function QtyRenderer({ data, row }: any) {
  const changeLineItem = data[0].changeLineItem;
  const isView = data[0].isView;
  const l = data[row.index];
  return (
    <Float
      name={`company-auto-qty-${row.index}`}
      value={l.qty}
      onChange={qty => changeLineItem(row.index, { qty: qty, amount: calculateAmount(l.rate, qty) })}
      min={0}
      max={999999.999}
      decimals={2}
      disabled={isView} />
  );
}



function RateRenderer({ data, row }: any) {
  const changeLineItem = data[0].changeLineItem;
  const isView = data[0].isView;
  const l = data[row.index];
  return (
    <Float
      name={`company-auto-rate-${row.index}`}
      value={l.rate}
      onChange={rate => {
        if (!!rate && l.qty === 0) {
          changeLineItem(row.index, { rate: rate, qty: 1, amount: calculateAmount(rate, l.qty) })
        }
        changeLineItem(row.index, { rate: rate, amount: calculateAmount(rate, l.qty) })

      }}
      decimals={2}
      min={0}
      max={999999.999}
      disabled={isView} />
  );
}


function AutoBillingInitializedImpl({
  autoBilling,
  autoBillingRef,
  setAutoBilling,
  onPopupClose,
}: AutoBillingLoadedDetailsViewParams) {
  const progressRef = useRef(false);
  const appContext = useContext(ApplicationContext);
  const systemSettings = useSystemSettings();

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

  const fmsGlobal = getFmsGlobal();
  const primaryCurrency = systemSettings.primaryCurrency ?? 'USD';
  const setSavedEntity = useSetEditedId([JobNames.GInvo]);
  var issueBillingDisable = (autoBilling.invoiceDetailList.length <= 0 || autoBilling.invoiceDate === null || autoBilling.invoiceDate === "");

  function subInvoList() {

    let nSeqNo = 0, nPrevComId = 0;


    autoBilling.invoiceDetailList.length = 0; //clear

    //Clear 버튼을 누르면 billingDetailList에 inv.seq 값이 0으로 바뀌어서 추가 
    const originalInvoiceSeq = autoBilling.billingDetailList.map(data => ({
      id: data.id,
      invoiceSeq: data.invoiceSeq
    }));

    autoBilling.billingDetailList.forEach(data => {
      data.invoiceSeq = 0;
    });

    autoBilling.billingDetailList
      .filter(data => data.selected)
      .forEach(data => {


        if (data.tbId !== nPrevComId) {
          nSeqNo++;
          nPrevComId = data.tbId;
        }

        if (data.amount > 0) {
          data.issue = "";
          data.invoiceSeq = nSeqNo;
        } else {
          data.issue = "N";
        }
      });


    autoBilling.billingDetailList
      .filter(data => data.selected)
      .forEach(data => {


        if (data.issue === '') {

          const detailIds: string[] = [];

          const totalAmount = autoBilling.billingDetailList
            .filter(w => w.selected && w.tbId === data.tbId && w.invoiceSeq === data.invoiceSeq)
            .reduce((sum, s) => sum + s.amount, 0);

          autoBilling.billingDetailList
            .filter(w => w.selected && w.tbId === data.tbId && w.invoiceSeq === data.invoiceSeq)
            .forEach(issuedRow => {
              issuedRow.issue = "Y";
              detailIds.push(issuedRow.id.toString());
            });


          const invoiceDetail: AutoInvoiceDetailList = {
            companyName: data.companyName,
            invoiceSeq: data.invoiceSeq,
            amount: totalAmount,
            customer: data.tbId,
            ids: detailIds.join(","),
            terms: data.terms,
            hdId: 0
          };

          autoBilling.invoiceDetailList.push(invoiceDetail)

        }

      });

    originalInvoiceSeq.forEach(item => {
      const billingItem = autoBilling.billingDetailList.find(data => data.id === item.id);
      if (billingItem) {
        billingItem.invoiceSeq = item.invoiceSeq;
      }
    });


    setAutoBilling({ invoiceDetailList: autoBilling.invoiceDetailList });


    issueBillingDisable = (autoBilling.invoiceDetailList.length <= 0 || autoBilling.invoiceDate === null || autoBilling.invoiceDate === "")

  }


  function toggleSelection(i: number) {
    const _list = _.cloneDeep(autoBilling.billingDetailList);

    _list[i].selected = !_list[i].selected;

    setAutoBilling({ billingDetailList: _list });
    subInvoList();
  }



  function changeLineItem(index: number, change: Partial<ICntrListItem>) {
    autoBilling.billingDetailList[index] = _.merge(autoBilling.billingDetailList[index], change);
    setAutoBilling({ billingDetailList: autoBilling.billingDetailList });
    subInvoList();
  }

  const totalAmount = autoBilling.invoiceDetailList.reduce((sum, item) => sum + item.amount, 0)

  function TotalLabelRenderer() {
    return <div className="ifms-input-ctr-row2-label"
      style={{ textAlign: 'right', marginRight: '5px', marginTop: '10px' }}>
      Total
    </div>;
  }

  function TotalAmountRenderer() {
    return <div className='mt-2 ' style={{ lineHeight: '15px' }}><Float decimals={2} disabled value={totalAmount} /></div>;
  }

  function cellValueChanged(rowIndex: number, accessor: string) {
    switch (accessor) {
      case 'selected':
        toggleSelection(rowIndex);
        break;
    }
  }



  const autoBillingDetailTableOptions: ApplicableTableSettings = {
    tableName: 'autoBillingDetailTable',
    ...autoBillingDetailTable,
    showNewRowPlaceholder: false,
    data: autoBilling?.billingDetailList.map((x, i) => ({ ...x, i, toggleSelection, changeLineItem })),
    useEditControls: false,
    rowRenderers: [
      { accessor: 'selected' },
      { accessor: 'companyName' },
      { accessor: 'billingCode' },
      { accessor: 'rate', EditCell: RateRenderer },
      { accessor: 'qty', EditCell: QtyRenderer },
      { accessor: 'amount' },
      { accessor: 'lastAmount' },
      { accessor: 'ar' },
      { accessor: 'invoiceSeq' },
    ]

    , getCellData: (accessor: any, rowData: any, rowIndex: number): CellData | undefined => {
      const curRowData = rowData as AutoBillingDetail;
      if (accessor === 'selected')
        return { type: TableCellTypes.Checkbox, value: curRowData.selected, onChangeFunc: (selected: boolean) => cellValueChanged(rowIndex, accessor) };
      else if (accessor === 'companyName')
        return { type: TableCellTypes.Text, value: curRowData.companyName };
      else if (accessor === 'billingCode')
        return { type: TableCellTypes.Text, value: curRowData.billingCode };
      else if (accessor === 'rate')
        return { type: TableCellTypes.Number, value: curRowData.rate, decimal: 2 };
      else if (accessor === 'qty')
        return { type: TableCellTypes.Number, value: curRowData.qty, decimal: 2 };
      else if (accessor === 'amount')
        return { type: TableCellTypes.Number, value: curRowData.amount, decimal: 2 };
      else if (accessor === 'lastAmount')
        return { type: TableCellTypes.Number, value: curRowData.lastAmount, decimal: 2 };
      else if (accessor === 'ar')
        return { type: TableCellTypes.Number, value: curRowData.ar, decimal: 2 };
      else if (accessor === 'invoiceSeq')
        return { type: TableCellTypes.Text, value: curRowData.invoiceSeq, align: TableCellAligns.Right };
      else
        return undefined;
    },
    isCellLock: (accessor: string, rowData: any): boolean => {
      if (accessor === 'billingCode' || accessor === 'companyName' || accessor === 'amount' || accessor === 'lastAmount' || accessor === 'ar' || accessor === 'invoiceSeq') return true;

      return false;
    },
    getCustomCellProps: (currentProps: any, cell: any) => {
      const l = autoBilling?.billingDetailList[cell.row.index];
      if (cell.column.id === 'lastAmount') {
        if (l.lastAmount !== l.amount) {
          currentProps.style.color = 'red';
        }
        else
          currentProps.style.color = 'black';

      }
      return currentProps;
    },
  };





  function IndicatorRenderer({ row }: any) {
    return <div style={{ paddingTop: '4px', width: '100%' }}>{row.index + 1}</div>;
  }


  const autoInvoiceDetailTableOptions: ApplicableTableSettings = {
    tableName: 'autoInvoiceDetailTable',
    ...autoInvoiceDetailTable,
    showNewRowPlaceholder: false,
    data: autoBilling?.invoiceDetailList.map((x, i) => ({ ...x, i, toggleSelection })),
    rowRenderers: [
      { accessor: 'indicator', Cell: IndicatorRenderer },
      { accessor: 'companyName', Cell: TextAlignLeftRenderer },
      { accessor: 'invoiceSeq', Cell: TextAlignRightRenderer },
      { accessor: 'amount', Cell: FloatRenderer },
    ],
    footerRow: [
      {
        colspan: 3,
        renderer: TotalLabelRenderer
      },
      {
        colspan: 1,
        renderer: TotalAmountRenderer
      },
    ],
  };




  async function setNextCheckNo() {
    const checkNo = await appContext.api.getAutoNo(FmsAutoNumberType.GeneralInvoNo, appContext.user?.branch);
    setAutoBilling({ invoiceNo: checkNo });
  }



  async function searchList() {

    let searchValue = autoBilling.search?.toUpperCase() ?? autoBilling.search;

    if (searchValue === null || searchValue === "") {
      await showGlobalOkDialog(<p>Please check the Company Name.</p>);
      return false;
    }

    if (autoBilling.billingDetailList?.length <= 0) {
      await showGlobalOkDialog(<p>There is no line items</p>);
      return false;
    }

    const filteredList = autoBilling.billingDetailList.filter(w =>
      w.companyName.toUpperCase().includes(searchValue)
    );

    autoBilling.billingDetailList.length = 0; //clear

    filteredList.forEach(detail => {
      const billingDetail = {
        companyName: detail.companyName,
        id: detail.id,
        hdId: detail.hdId,
        tbId: detail.tbId,
        billingCode: detail.billingCode,
        rate: detail.rate,
        qty: detail.qty,
        amount: detail.amount,
        lastAmount: detail.lastAmount,
        ar: detail.ar,
        invoiceSeq: detail.invoiceSeq,
        issue: detail.issue,
        terms: detail.terms,
        selected: detail.selected
      };
      autoBilling.billingDetailList.push(billingDetail);
    });


    subInvoList();
  }




  function selectAllInTable() {
    const _list = _.cloneDeep(autoBilling.billingDetailList);
    _list.forEach(item => item.selected = true);
    setAutoBilling({ billingDetailList: _list });
    subInvoList();
  }

  function clearTable() {
    const _list = _.cloneDeep(autoBilling.billingDetailList);
    _list.forEach(item => item.selected = false);
    setAutoBilling({ billingDetailList: _list });
    subInvoList();
  }

  async function getList() {

    const getInvoiceDate = addMonths(getDate(autoBilling.invoiceDate) ?? "", -1);
    const year = parseInt(getInvoiceDate?.substring(0, 4) ?? "");
    const month = parseInt(getInvoiceDate?.substring(5, 7) ?? "");

    const result = await appContext.api.abinvDetailSelect(year, month, autoBilling.group);


    if (autoBilling.invoiceDate === null || autoBilling.invoiceDate === "") {
      await showGlobalOkDialog(<p>Please check the Invoice date</p>);
    }


    if (result) {
      setAutoBilling({ billingDetailList: result.result });
    }

    subInvoList();
  }

  async function setIssueBilling() {

    const ok = await appContext.api.checkSettingAutoNo(FmsAutoNumberType.GeneralInvoNo,
      (fmsGlobal.UseBranch ? (appContext.suntekSettings?.company?.defaultBranch === null ? "HQ" : appContext.suntekSettings?.company?.defaultBranch) : ""))

    if (!ok) {
      await showGlobalOkDialog(<p>Please enter General Invoice # or setup Auto No in Options {'>'} Auto Numbers</p>);
      return;
    }

    if (autoBilling.invoiceDate === null || autoBilling.invoiceDate === "") {
      await showGlobalOkDialog(<p>Please check the Invoice date</p>);
    }

    if (await subSaveAutoBilling()) {
      if (onPopupClose) {
        onPopupClose();
      }

    } else {
      await showGlobalOkDialog(<p>failed</p>);
    }


    return true;

  }


  async function subSaveAutoBilling() {
    let sqlInfos: SQLInfo[] = [];

    try {

      if (autoBilling.billingDetailList.length === 0) {
        return true;
      }

      autoBilling.billingDetailList.filter(w => w.selected).forEach(billingDetailList => {
        sqlInfos.push(new SQLInfo({
          tableName: "T_AOTHERINFO",
          type: SQLType.Update,
          fields: [
            new SQLField({ fieldName: 'F_F1', propertyName: 'F1', value: billingDetailList.rate }),
            new SQLField({ fieldName: 'F_F2', propertyName: 'F2', value: billingDetailList.qty }),
            new SQLField({ fieldName: "F_F3", propertyName: "F3", value: billingDetailList.amount }),
          ],
          key: new SQLField({ fieldName: "F_Id", propertyName: "Id", value: billingDetailList.id }),
        }));
      });



      const apiResult = await appContext.api.saveBillingDetailList(sqlInfos);
      if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog(<p>Failed !</p>);
        return false;
      }


      const allIds = autoBilling.invoiceDetailList.flatMap(detail => detail.ids).join(',');

      const apiResult2 = await appContext.api.selectAotherTemp(allIds);
      if (apiResult2 === null || !apiResult2.result) {
        showGlobalOkDialog(<p>Failed !</p>);
        return false;
      }
      const aotherTemp: AotherTemp[] = apiResult2.result;

      let invoInsertId = -1;

      autoBilling.invoiceDetailList.forEach(invoiceDetailList => {

        let invoDetailIds = invoiceDetailList.ids.split(',').map(x => parseInt(x));
        let curAotherTemp = aotherTemp.filter(x => invoDetailIds.indexOf(x.id) > -1);
        // console.log(curAotherTemp);

        sqlInfos.push(new SQLInfo({
          tableName: "T_INVOHD",
          type: SQLType.Insert,
          fields: [
            new SQLField({ fieldName: 'F_Billto', propertyName: 'Billto', value: invoiceDetailList.customer }),
            new SQLField({ fieldName: 'F_shipto', propertyName: 'Shipto', value: invoiceDetailList.customer }),
            new SQLField({ fieldName: "F_Terms", propertyName: "Terms", value: invoiceDetailList.terms }),

            new SQLField({ fieldName: "F_Currency", propertyName: "Currency", value: primaryCurrency }),
            new SQLField({ fieldName: "F_InvoiceNo", propertyName: "InvoiceNo", value: '' }),
            new SQLField({ fieldName: "F_InvoiceAmt", propertyName: "InvoiceAmt", value: invoiceDetailList.amount }),

            new SQLField({ fieldName: 'F_DueDate', propertyName: 'DueDate', value: addDays(getDate(autoBilling.invoiceDate) ?? "", invoiceDetailList.terms) }),
            new SQLField({ fieldName: 'F_PostDate', propertyName: 'PostDate', value: autoBilling.invoiceDate }),
            new SQLField({ fieldName: "F_InvoiceDate", propertyName: "InvoiceDate", value: autoBilling.invoiceDate }),

            new SQLField({ fieldName: 'F_TLAMOUNT', propertyName: 'TLAMOUNT', value: invoiceDetailList.amount }),
            new SQLField({ fieldName: 'F_RCVGLNO', propertyName: 'RCVGLNO', value: fmsGlobal.ARGLNo }),
            new SQLField({ fieldName: "F_U1ID", propertyName: "U1ID", value: systemSettings.userId ?? '' }),

            new SQLField({ fieldName: 'F_U1DATE', propertyName: 'U1DATE', value: "GetDate()" }),
            new SQLField({ fieldName: 'F_U2ID', propertyName: 'U2ID', value: systemSettings.userId ?? '' }),
            new SQLField({ fieldName: "F_U2DATE", propertyName: "U2DATE", value: "GetDate()" }),
            new SQLField({ fieldName: "F_ExRate1", propertyName: 'InvExRate1', value: 1}),  //ExRate값을 강제로 추가
            new SQLField({ fieldName: "F_CUR1", propertyName: 'F_Cur1', value: primaryCurrency})  //Cur1값을 강제로 추가

          ],
          key: new SQLField({ fieldName: "F_Id", propertyName: "Id", value: invoInsertId }),
        }));


        sqlInfos.push(new SQLInfo({
          tableName: "T_AOTHERLOCAL",
          type: SQLType.Insert,
          fields: [
            new SQLField({ fieldName: 'F_TBNAME', propertyName: 'TBNAME', value: "T_INVOHD" }),
            new SQLField({ fieldName: 'F_TBID', propertyName: 'TBID', value: invoInsertId }),
            new SQLField({ fieldName: "F_C1", propertyName: "C1", value: autoBilling.invoiceRemarks }),
            new SQLField({ fieldName: "F_TYPE", propertyName: "TYPE", value: 1 }),
          ],
        }));


        curAotherTemp.forEach(aothertemp => {
          sqlInfos.push(new SQLInfo({
            tableName: "T_INVODETAIL",
            type: SQLType.Insert,
            fields: [
              new SQLField({ fieldName: 'F_INVOHDID', propertyName: 'INVOHDID', value: invoInsertId }),
              new SQLField({ fieldName: 'F_BILLINGCODE', propertyName: 'BILLINGCODE', value: aothertemp.c1 }),
              new SQLField({ fieldName: "F_Description", propertyName: "F3", value: aothertemp.c2 }),

              new SQLField({ fieldName: 'F_RATE', propertyName: 'RATE', value: aothertemp.f1 }),
              new SQLField({ fieldName: 'F_QTY', propertyName: 'QTY', value: aothertemp.f2 }),
              new SQLField({ fieldName: "F_Amount", propertyName: "Amount", value: aothertemp.f3 }),

              new SQLField({ fieldName: 'F_LAMOUNT', propertyName: 'LAmount', value: aothertemp.f3 }),
              new SQLField({ fieldName: 'F_CUR', propertyName: 'CUR', value: primaryCurrency }),
              new SQLField({ fieldName: "F_CURAMOUNT", propertyName: "CURAMOUNT", value: aothertemp.f3 }),

              new SQLField({ fieldName: 'F_CHGTYPE', propertyName: 'CHGTYPE', value: '9' }),
              new SQLField({ fieldName: 'F_EXRATE', propertyName: 'EXRATE', value: '1' }),

            ],
          }));
        });


        function addNonNull(array: SQLInfo[], item: SQLInfo | null): void {
          if (item !== null) {
            array.push(item);
          }
        }

        addNonNull(sqlInfos, historyUpdate("T_INVOHD", invoInsertId, systemSettings.userId ?? '', 'Auto Billing'));
        addNonNull(sqlInfos, saveAcctHistory(invoInsertId, 'I', 0));
        addNonNull(sqlInfos, SaveNewJournalByTask(JobNames.Invoice, invoInsertId, systemSettings.userId ?? ''));

        invoInsertId--;

      });

      sqlInfos.push(new SQLInfo({
        tableName: "T_AOTHERINFO",
        type: SQLType.Update,
        fields: [
          new SQLField({ fieldName: 'F_C10', propertyName: 'C10', value: "" }),
        ],
        where: "F_TBNAME = 'T_company' AND F_TYPE= 1 AND F_C10 <> ''"
      }));


      const apiSaveResult: ApiResult<InsertResultsWithData> = await appContext.api.saveJobDetail(
        JobNames.AutoBillingInvoice,
        sqlInfos
      );

      if (!apiSaveResult || !apiSaveResult.success) {
        await showGlobalOkDialog(<p>Failed !</p>);
        return false;
      }


      const insertResult = apiSaveResult.result.insertList.find(x => x.model === 'InvoHd');
      if (insertResult) {
        invoInsertId = insertResult.newId;
      }

      setSavedEntity(invoInsertId.toString(), false, false, true);


    } catch (error) {
      await showGlobalYesNoDialog(
        <div>
          <h3>Error</h3>
        </div>
      );
    }

    return true;

  }


  return (
    <div className='report-popup' >
      <div >
        <div style={{ display: 'flex' }}>
          <div style={{ width: '250px' }}>
            <TextBox value={autoBilling.search} onChange={e => setAutoBilling({ search: e.target.value })} />
          </div>
          <div style={{ marginLeft: '10px' }}>
            <SecondaryButton focusable={true} size='small' style={{ width: '80px' }} onClick={searchList}>Search</SecondaryButton>
          </div>
        </div>

        <div style={{ display: 'flex' }}>
          <div style={{ width: '290px', display: 'inline-block' }}>
            <div style={{ height: '20px', marginTop: '10px' }}>
              <Label bold label="Invoice Remarks" />
            </div>
            <MultilineTextBox
              maxLength={600}
              style={{ height: '40px' }}
              value={autoBilling.invoiceRemarks}
              onChange={e => setAutoBilling({ invoiceRemarks: e.target.value })}
            />
          </div>

          <div style={{ marginLeft: '125px' }}>
            <div style={{ marginTop: '30px' }}>
              <InlineInputControlRow bold mode="w80" label="Invoice Date">
                <div style={{ width: '80px' }}>
                  <DateControl
                    value={autoBilling.invoiceDate}
                    onChange={invoiceDate => setAutoBilling({ invoiceDate })}
                  />
                </div>
              </InlineInputControlRow>
            </div>

            <div>
              <InlineInputControlRow bold label="Group" mode="w80">
                <div style={{ width: '80px' }}>
                  <TextBox value={autoBilling.group} onChange={e => setAutoBilling({ group: e.target.value })} />
                </div>
              </InlineInputControlRow>
            </div>
          </div>

          <div style={{ marginLeft: '50px', marginTop: '30px' }}>
            <InlineInputControlRow
              label={<span className="input-control-row-label-link">Invoice #</span>}
              mode="w70"

              onLabelClick={setNextCheckNo}
            >
              <div style={{ width: '85px' }}>
                <TextBox placeholder="Auto Number" disabled={true} value={autoBilling.invoiceNo} onChange={e => true} />
              </div>
            </InlineInputControlRow>
          </div>
        </div>



        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end'
        }}>
          <Label style={{ position: 'relative', top: '5px' }} label='Billing Items' bold />
          <div style={{
            display: "flex",
            float: 'right',
            justifyContent: 'space-between',
            width: 160,
            paddingBottom: 5,
            marginTop: 10
          }}>
            <SecondaryButton size='extrasmall' onClick={getList}>View</SecondaryButton>
            <PrimaryButton size='extrasmall' onClick={selectAllInTable}>ALL</PrimaryButton>
            <PrimaryButton size='extrasmall' onClick={clearTable}>CLEAR</PrimaryButton>
          </div>
        </div>

        <div className='report-row mt-1'>
          <EditableTable
            tableOptions={autoBillingDetailTableOptions}
            deleteLine={() => { return false }}
            addLine={() => { return undefined }}

          />
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end'
        }}>
          <Label style={{ position: 'relative', top: '5px' }} label='Invocie List' bold />

        </div>

        <div className='report-row mt-1' >
          <SmartTable
            {...autoInvoiceDetailTableOptions}
          />
        </div>

      </div>

      <div className='clearfix' />
      <div className='report-footer'>

        <SecondaryButton
          focusable={true}
          disabled={issueBillingDisable}
          size="regular"
          style={{ width: '90px' }}
          onClick={() => setIssueBilling()}>
          Issue Billings
        </SecondaryButton>

        <SecondaryButton
          focusable={true}
          size="regular"
          style={{ width: '90px' }}
          onClick={onPopupClose}>
          CLOSE
        </SecondaryButton>
      </div>

    </div>
  );
}


const AutoBillingDetails = ({
  onPopupClose,
}: { onPopupClose: () => void }) => {

  /**
  * Implementation note:
  *   key is important in this case in order to force full List view rerender for different parameters.
  */

  return (
    <AutoBillingImpl onPopupClose={onPopupClose}
      key={`auobilling-details-`} />
  );
};
export default AutoBillingDetails;